/**
 * Help Center page - Green/black theme, PublicPageLayout.
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../providers';
import SEO from '../components/seo/SEO';
import { generateFAQSchema } from '../utils/seo';
import { PublicPageLayout } from '../components/public';

const HelpCenter = () => {
  const navigate = useNavigate();
  const { language } = useI18n();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqCategories = [
    {
      id: 'getting-started',
      icon: '🚀',
      title: language === 'fr' ? 'Démarrage' : 'Getting Started',
      questions: [
        { q: language === 'fr' ? 'Comment créer un compte?' : 'How do I create an account?', a: language === 'fr' ? 'Créez un compte en utilisant votre numéro de téléphone camerounais (+237). Vous recevrez un code OTP par SMS pour vérifier votre numéro. C\'est gratuit et prend moins d\'une minute!' : 'Create an account using your Cameroonian phone number (+237). You\'ll receive an OTP code via SMS to verify your number. It\'s free and takes less than a minute!' },
        { q: language === 'fr' ? 'Qu\'est-ce que "3 Taps to Trust"?' : 'What is "3 Taps to Trust"?', a: language === 'fr' ? 'C\'est notre philosophie: créer une transaction, partager le lien, et sécuriser - en 3 étapes simples.' : 'It\'s our philosophy: create a transaction, share the link, and secure - in 3 simple steps.' },
        { q: language === 'fr' ? 'Comment créer ma première transaction?' : 'How do I create my first transaction?', a: language === 'fr' ? 'Allez sur "Créer une Transaction", remplissez les détails (titre, montant, délai de livraison), et partagez le lien généré.' : 'Go to "Create Transaction", fill in the details (title, amount, delivery deadline), and share the generated link.' },
      ],
    },
    {
      id: 'payments',
      icon: '💳',
      title: language === 'fr' ? 'Paiements' : 'Payments',
      questions: [
        { q: language === 'fr' ? 'Quels modes de paiement sont acceptés?' : 'What payment methods are accepted?', a: language === 'fr' ? 'Nous acceptons MTN Mobile Money, Orange Money, M-Pesa, Airtel Money, et les paiements par carte via Flutterwave.' : 'We accept MTN Mobile Money, Orange Money, M-Pesa, Airtel Money, and card payments via Flutterwave.' },
        { q: language === 'fr' ? 'Combien coûtent les frais?' : 'How much are the fees?', a: language === 'fr' ? 'Les frais de transaction sont de 1,5% du montant, plafonnés à 15,000 XAF.' : 'Transaction fees are 1.5% of the amount, capped at 15,000 XAF.' },
        { q: language === 'fr' ? 'Combien de temps prend le traitement du paiement?' : 'How long does payment processing take?', a: language === 'fr' ? 'Les paiements mobiles sont généralement traités instantanément.' : 'Mobile payments are usually processed instantly.' },
      ],
    },
    {
      id: 'transactions',
      icon: '📦',
      title: language === 'fr' ? 'Transactions' : 'Transactions',
      questions: [
        { q: language === 'fr' ? 'Comment fonctionne l\'escrow?' : 'How does escrow work?', a: language === 'fr' ? 'L\'argent est détenu en escrow jusqu\'à confirmation de livraison. L\'acheteur peut approuver ou soulever un litige. Fonds libérés après 72h si rien.' : 'Money is held in escrow until delivery is confirmed. Buyer can approve or raise a dispute. Funds released after 72h if no action.' },
        { q: language === 'fr' ? 'Que se passe-t-il si je n\'approuve pas la livraison?' : 'What happens if I don\'t approve delivery?', a: language === 'fr' ? 'Vous pouvez soulever un litige dans les 72 heures. Sinon les fonds sont libérés au vendeur après 72h.' : 'You can raise a dispute within 72 hours. Otherwise funds are released to the seller after 72h.' },
        { q: language === 'fr' ? 'Puis-je annuler une transaction?' : 'Can I cancel a transaction?', a: language === 'fr' ? 'Oui, avant le paiement. Après paiement, livraison ou litige.' : 'Yes, before payment. After payment, delivery or dispute.' },
      ],
    },
    {
      id: 'disputes',
      icon: '⚠️',
      title: language === 'fr' ? 'Litiges' : 'Disputes',
      questions: [
        { q: language === 'fr' ? 'Comment soulever un litige?' : 'How do I raise a dispute?', a: language === 'fr' ? 'Page de la transaction → "Soulever un Problème" → type, description, preuves.' : 'Transaction page → "Raise Issue" → type, description, evidence.' },
        { q: language === 'fr' ? 'Combien de temps dure la négociation?' : 'How long does negotiation last?', a: language === 'fr' ? '7 jours. Puis médiation administrative possible.' : '7 days. Then administrative mediation if needed.' },
        { q: language === 'fr' ? 'Qui prend la décision finale?' : 'Who makes the final decision?', a: language === 'fr' ? 'Un administrateur WekaTrust en médiation. Décisions finales et contraignantes.' : 'A WekaTrust administrator in mediation. Decisions are final and binding.' },
      ],
    },
    {
      id: 'wallet',
      icon: '💰',
      title: language === 'fr' ? 'Portefeuille' : 'Wallet',
      questions: [
        { q: language === 'fr' ? 'Comment retirer mes fonds?' : 'How do I withdraw my funds?', a: language === 'fr' ? 'Portefeuille → Retirer → méthode (MTN MoMo, Orange Money), montant, confirmer. 1-3 jours ouvrables.' : 'Wallet → Withdraw → method (MTN MoMo, Orange Money), amount, confirm. 1-3 business days.' },
        { q: language === 'fr' ? 'Y a-t-il des frais de retrait?' : 'Are there withdrawal fees?', a: language === 'fr' ? 'Oui, 1% (minimum 100 XAF).' : 'Yes, 1% (minimum 100 XAF).' },
        { q: language === 'fr' ? 'Solde disponible vs en attente?' : 'Available vs pending balance?', a: language === 'fr' ? 'Disponible = retirable. En attente = lié aux transactions en cours.' : 'Available = withdrawable. Pending = tied to ongoing transactions.' },
      ],
    },
    {
      id: 'verification',
      icon: '🆔',
      title: language === 'fr' ? 'Vérification' : 'Verification',
      questions: [
        { q: language === 'fr' ? 'Quels sont les niveaux de vérification?' : 'What are the verification levels?', a: language === 'fr' ? 'Niveau 0: téléphone. Niveau 1: téléphone + email. Niveau 2: KYC avec pièce d\'identité.' : 'Level 0: phone. Level 1: phone + email. Level 2: KYC with ID.' },
        { q: language === 'fr' ? 'Pourquoi devrais-je me vérifier?' : 'Why should I verify?', a: language === 'fr' ? 'Crédibilité et limite de transaction plus élevées.' : 'Higher credibility and transaction limit.' },
        { q: language === 'fr' ? 'Combien de temps prend la vérification KYC?' : 'How long does KYC verification take?', a: language === 'fr' ? 'Généralement 1-3 jours ouvrables.' : 'Typically 1-3 business days.' },
      ],
    },
  ];

  const allFaqs = useMemo(() => faqCategories.flatMap((c) => c.questions.map((q) => ({ question: q.q, answer: q.a }))), []);
  const faqStructuredData = useMemo(() => generateFAQSchema(allFaqs), [allFaqs]);

  const filteredCategories = faqCategories.map((cat) => ({
    ...cat,
    questions: cat.questions.filter(
      (q) =>
        !searchQuery ||
        q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
        q.a.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter((cat) => cat.questions.length > 0);

  const toggleFaq = (id) => setExpandedFaq(expandedFaq === id ? null : id);

  return (
    <PublicPageLayout>
      <SEO
        title={language === 'fr' ? "Centre d'Aide - FAQ et Support | WekaTrust" : 'Help Center - FAQs & Support | WekaTrust'}
        description={language === 'fr' ? 'Trouvez des réponses aux questions courantes sur WekaTrust, paiements, litiges.' : 'Find answers to common questions about WekaTrust, payments, disputes.'}
        keywords={language === 'fr' ? 'WekaTrust, aide, FAQ, support, escrow, Cameroun' : 'WekaTrust, help, FAQ, support, escrow, Cameroon'}
        url="/help"
        locale={language}
        structuredData={faqStructuredData}
      />
      <button type="button" onClick={() => navigate('/')} className="public-page-back">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
        {language === 'fr' ? "Retour à l'accueil" : 'Back to Home'}
      </button>

      <div className="public-page-hero">
        <h1 className="public-page-title">{language === 'fr' ? "Centre d'Aide" : 'Help Center'}</h1>
        <p className="public-page-sub">
          {language === 'fr' ? 'Trouvez des réponses à vos questions' : 'Find answers to your questions'}
        </p>
      </div>

      <div className="public-page-section" style={{ marginBottom: '1.5rem' }}>
        <div className="public-page-card" style={{ marginBottom: 0 }}>
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={language === 'fr' ? 'Rechercher dans les FAQ...' : 'Search FAQs...'}
          />
        </div>
      </div>

      <div className="public-page-section">
        {filteredCategories.map((category) => (
          <div key={category.id} className="public-page-section" style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <span className="public-page-contact-icon" style={{ width: '2.5rem', height: '2.5rem', fontSize: '1.25rem' }}>{category.icon}</span>
              <h2 className="public-page-section-title" style={{ marginBottom: 0 }}>{category.title}</h2>
            </div>
            {category.questions.map((faq, idx) => {
              const faqId = `${category.id}-${idx}`;
              const isOpen = expandedFaq === faqId;
              return (
                <div key={faqId} className="public-page-faq-item">
                  <button type="button" className="public-page-faq-q" onClick={() => toggleFaq(faqId)} aria-expanded={isOpen}>
                    {faq.q}
                    <span style={{ flexShrink: 0 }}>{isOpen ? '−' : '+'}</span>
                  </button>
                  {isOpen && <div className="public-page-faq-a">{faq.a}</div>}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div className="public-page-card" style={{ textAlign: 'center', padding: '2rem' }}>
        <div className="public-page-contact-icon" style={{ margin: '0 auto 1rem' }}>💬</div>
        <h2 className="public-page-section-title" style={{ marginBottom: '0.5rem' }}>{language === 'fr' ? "Besoin d'Aide Supplémentaire?" : 'Still Need Help?'}</h2>
        <p className="public-page-card-desc" style={{ marginBottom: '1.5rem' }}>
          {language === 'fr' ? "Notre équipe de support est là pour vous aider" : 'Our support team is here to help you'}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'center' }}>
          <button type="button" onClick={() => navigate('/contact')} className="public-page-cta">
            {language === 'fr' ? 'Nous Contacter' : 'Contact Us'}
          </button>
          <button type="button" onClick={() => navigate('/')} className="public-page-cta" style={{ background: 'transparent', color: 'var(--pub-green)', border: '1px solid var(--pub-green)' }}>
            {language === 'fr' ? "Retour à l'Accueil" : 'Back to Home'}
          </button>
        </div>
      </div>
    </PublicPageLayout>
  );
};

export default HelpCenter;
